import {
  MIRA_BRANCH_NONE,
  MIRA_BRANCH_OTHER,
  MiraAvailabilityState,
  MiraBranchDocument,
  MiraPublishedMetric,
  MiraSectionDocument,
} from "@/data/models/Mira";
import { MiraViewContext, formatPercent, publishedMetric } from "@/utils/MiraDataQualityView";
import { caseCountIsValue, casesText } from "@/utils/MiraCaseUnits";
import { compareBestFirst, metricDirection } from "@/utils/MiraMetricDirection";
import { isTotalMetric } from "@/utils/MiraTrendView";

/**
 * The Mira comparison view model (slice 1: the member clients of a group): one headline figure set
 * side by side across the group's companies, as a bar per member against the group total.
 *
 * Pure, like the other Mira view models: it takes the own (group) document, the members' own
 * documents, their names and a value formatter, and never mounts anything or reads a repository. The
 * formatter is the screen's own (`*TrendFormatter`), so a member's bar reads in the same unit as the
 * tile it was selected from: days, a share, a count, MWh.
 *
 * Decided by Taimoor on Hans's Track #2610009 (2026-10-05): "select comparisons of data: clients in a
 * group; then we use only sum / mean values". A member's figure is exactly the headline its own tile
 * would show (the sum for a total, the mean otherwise, the share for a share), which is the published
 * period value, so this model never recomputes a statistic.
 */

/**
 * A comparison, shared across the Mira screens through `MiraComparisonModule`. Since 2026-11-02 (Hans,
 * #2610137) two are chosen independently: one **upwards** (a broader scope, drawn left of the company's own
 * bar: the Markt, a member's Gruppe) and one **downwards** (the company's own parts, right of its bar: the
 * Mandanten of a group, the Standorte), each with its own icon.
 */
export type MiraComparisonMode = "NONE" | "MEMBERS" | "BRANCHES" | "INSURERS" | "MARKET" | "GROUP";

export const MIRA_COMPARISON_NONE: MiraComparisonMode = "NONE";
export const MIRA_COMPARISON_MEMBERS: MiraComparisonMode = "MEMBERS";
/** The branches (Standorte) of a client that is not a group master. */
export const MIRA_COMPARISON_BRANCHES: MiraComparisonMode = "BRANCHES";
/**
 * The Markt comparison (#2610072): the company against "Markt je Projekt" (Markt je Mandant dropped,
 * #2610112 X6a), drawn like the member comparison ({@link buildMarketComparison}). Orthogonal to the member / branch
 * comparisons (it sits beside them in the selector), offered whenever a figure on the screen has a Markt
 * value.
 */
export const MIRA_COMPARISON_MARKET: MiraComparisonMode = "MARKET";
/**
 * A group member's view of its group (#2610112 X6d): its own value against the group's, from the member's
 * own `groupView_{section}` document. Offered to a member only, never to a master ({@link buildGroupViewComparison}).
 */
export const MIRA_COMPARISON_GROUP: MiraComparisonMode = "GROUP";

/** The comparisons upwards (a broader scope than the company), in the order the picker lists them. */
export const MIRA_UPWARD_COMPARISONS: MiraComparisonMode[] = [MIRA_COMPARISON_GROUP, MIRA_COMPARISON_MARKET];
/**
 * The insurers the company works for (#2610137, 2026-11-03): its value against each insurer's projects, from
 * the per-insurer document (`insurers_{section}`, a master's pooled `group_insurers_{section}`).
 */
export const MIRA_COMPARISON_INSURERS: MiraComparisonMode = "INSURERS";

/**
 * The comparisons downwards (the company's own parts): Mandanten or Standorte (never both), and the insurers.
 */
export const MIRA_DOWNWARD_COMPARISONS: MiraComparisonMode[] = [MIRA_COMPARISON_MEMBERS, MIRA_COMPARISON_BRANCHES, MIRA_COMPARISON_INSURERS];

/** How many member rows show before the "Alle anzeigen" / "Weniger anzeigen" fold (the card's job). */
export const MIRA_COMPARISON_FOLD = 10;

/** One bar in the comparison card: a member (or the group total, when {@link reference}). */
export interface MiraComparisonRow {
  /** The member's client id; the reference row carries {@link MIRA_COMPARISON_REFERENCE_ID}. */
  id: string;
  label: string;
  /** The headline value, in the figure's own terms (a share stays a share), for the sort. */
  value: number;
  /** The value formatted in the figure's unit, the sign kept (INS-006's overrun can be negative). */
  valueText: string;
  /**
   * A total only (Projekte, Gerätetage, ...): the row's share of the reference total ("44 %"), shown before
   * the value (Hans, #2610112 X1 / X2). `null` for an average or a share, and on the reference row.
   */
  shareText: string | null;
  /**
   * The cases behind the value ("412 Fälle"), or `null` for a total, whose count is not in projects
   * (Hans, Track #2610072: always show the case count).
   */
  casesText: string | null;
  /** How much of the track the bar fills, 0 to 1: `|value|` over the largest `|value|` of the members. */
  fraction: number;
  /** The neutral change against this row's own comparison period, or `null` where there is none. */
  deltaText: string | null;
  deltaDirection: "up" | "down" | "flat";
  /** Whether the card shows a change column (the chosen period has a comparison). Uniform across rows. */
  showDelta: boolean;
  /** The group-total reference row, drawn distinctly from the members. */
  reference: boolean;
  /**
   * The Markt only (#2610072): this row's own P15 / median / P85 on the shared bar scale, so the company's
   * and each Markt entity's four figures read against each other. Absent in the member and branch views,
   * which draw the reference's spread on every row instead ({@link MiraComparisonView.markers}).
   */
  markers?: MiraComparisonMarkers;
}

/** The id of the reference (group total) row, kept out of the member id space. */
export const MIRA_COMPARISON_REFERENCE_ID = "__reference__";

/** The comparison card's data: the reference row, the member rows, and the members with no value. */
export interface MiraComparisonView {
  /** The group total (or the company total, for branches), or `null` where it publishes no value. */
  reference: MiraComparisonRow | null;
  /**
   * The members (or branches) with a published value, bar per row, best first (Hans, #2610112 X6c):
   * ascending where lower is better, else descending, ties by name ({@link metricDirection}).
   */
  rows: MiraComparisonRow[];
  /**
   * A total: the reference row is the whole the rows add up to, so it shows as a figure without a bar
   * (Hans, #2610112 X1: "do not show the bar for total"). Absent for an average and in the Markt.
   */
  referenceWithoutBar?: boolean;
  /**
   * The reference (the company's own value) is listed after the rows: the Markt and a member's group are
   * the broader scope, and the broader scope comes first (Hans, #2610137). Absent: the reference first.
   */
  referenceLast?: boolean;
  /** The members (or branches) with no published value in the period, by name: one "Ohne Wert" line. */
  withoutValue: string[];
  /**
   * Branch comparison only: the figure is not carried by the branch document at all (DEV-006, PRJ-006,
   * PRJ-008, PHA-001/002), so there is no branch comparison for it. The card then shows a reason line in
   * place of empty bars, rather than listing every branch as "Ohne Wert". Absent for the member view.
   */
  noComparisonForMetric?: boolean;
  /** The i18n key of that reason line where it is not the branches' (the insurers', 2026-11-03). */
  noComparisonKey?: string;
  /**
   * The i18n key of the line naming the rows without a value, `{0}` the names; absent means "Ohne Wert".
   * The Markt says why instead: too few companies with enough cases (#2610072).
   */
  withoutValueKey?: string;
  /**
   * The reference's own spread over its projects (Hans, Track #2610040 R3 / #2610047 Q1, answer (b)): its
   * P15, median and P85 as positions on the rows' bar scale, so every company / branch reads against the
   * reference's distribution. Only for a figure that publishes a spread (an average, never a total or a
   * share) and only where the screen may show percentiles (`VIEW_MIRA_PERCENTILES`); absent otherwise.
   */
  markers?: MiraComparisonMarkers;
}

/** The reference's P15 / median / P85 as fractions of the bar scale, and their values as text. */
export interface MiraComparisonMarkers {
  p15: number;
  median: number;
  p85: number;
  /** "P15 3 Tage · Median 7 Tage · P85 21 Tage", in the figure's unit. */
  text: string;
}

/** A published figure's spread, when it carries all three values. */
export interface ReferenceSpread {
  p15: number;
  median: number;
  p85: number;
}

/** The reference figure's P15 / median / P85 for a period, or `null` where any is missing or negative. */
export function referenceSpread(document: MiraSectionDocument | null | undefined, periodKey: string, metricId: string): ReferenceSpread | null {
  const metric = publishedMetric(document?.periods[periodKey] ?? null, metricId);
  const { p15, median, p85 } = metric ?? {};
  // All three or none; a negative spread (INS-006's overrun can be) has no place on an absolute bar scale.
  if (typeof p15 !== "number" || typeof median !== "number" || typeof p85 !== "number" || p15 < 0) {
    return null;
  }
  return { p15, median, p85 };
}

/** Whether the screen's formatter for this figure is the share formatter (it formats like `formatPercent`). */
function isShareFormatter(formatValue: (value: number) => string, ctx: MiraViewContext): boolean {
  return formatValue(0.37) === formatPercent(0.37, ctx);
}

/**
 * The neutral change of one row against its own comparison period, in the figure's unit: "+1,2 Tage",
 * "−3 %", "±0". Grey wherever it is shown (a member higher or lower than last period is neither good
 * nor bad). `direction` is read from the *formatted* values, so a change too small to show reads as
 * unchanged rather than as "+0,0".
 */
function comparisonDelta(
  current: number,
  previous: number | null | undefined,
  hasComparisonPeriod: boolean,
  formatValue: (value: number) => string,
  ctx: MiraViewContext
): { text: string | null; direction: "up" | "down" | "flat"; showDelta: boolean } {
  if (previous === null || previous === undefined) {
    return { text: null, direction: "flat", showDelta: hasComparisonPeriod };
  }

  const changed = formatValue(current) !== formatValue(previous);
  const direction = !changed ? "flat" : current > previous ? "up" : "down";
  const sign = direction === "up" ? "+" : direction === "down" ? "−" : "±";
  const difference = direction === "flat" ? 0 : Math.abs(current - previous);
  // A share changes in percentage points, as on the tiles ("+3 PP"): "+3 %" would read as relative.
  if (isShareFormatter(formatValue, ctx)) {
    const points = ctx.t("mira.format.deltaPoints", [`${sign}${ctx.formatNumber(Math.round(difference * 100))}`]);
    return { text: points, direction, showDelta: true };
  }
  return { text: `${sign}${formatValue(difference)}`, direction, showDelta: true };
}


/** The published headline value for a metric and period, or `null` where the period has no value. */
export function headlineValue(document: MiraSectionDocument | null | undefined, periodKey: string, metricId: string): number | null {
  const metric = publishedMetric(document?.periods[periodKey] ?? null, metricId);
  return metric?.value ?? null;
}

/** The case count behind a document's AVAILABLE headline value, or `null` (none published, or no basis). */
export function headlineBasis(document: MiraSectionDocument | null | undefined, periodKey: string, metricId: string): number | null {
  const metric = publishedMetric(document?.periods[periodKey] ?? null, metricId);
  return metric?.basis ?? null;
}

/** The AVAILABLE value of one branch's metric for a period, or `null` (absent, or a real "nothing"). */
function branchValue(
  document: MiraBranchDocument | null | undefined,
  periodKey: string,
  branchId: string,
  metricId: string
): number | null {
  const metric: MiraPublishedMetric | undefined = document?.periods[periodKey]?.branches[branchId]?.[metricId];
  return metric && metric.state === MiraAvailabilityState.AVAILABLE && metric.value !== null && metric.value !== undefined
    ? metric.value
    : null;
}

/** The case count behind one branch's AVAILABLE value for a period, or `null`. */
function branchBasis(
  document: MiraBranchDocument | null | undefined,
  periodKey: string,
  branchId: string,
  metricId: string
): number | null {
  const metric: MiraPublishedMetric | undefined = document?.periods[periodKey]?.branches[branchId]?.[metricId];
  return metric && metric.state === MiraAvailabilityState.AVAILABLE ? metric.basis ?? null : null;
}

/** "412 Projekte" for a case count in the figure's own unit, or `null` where there is none. */
function rowCasesText(metricId: string, basis: number | null, ctx: MiraViewContext): string | null {
  return basis === null ? null : casesText(metricId, basis, ctx);
}

/**
 * One row source for the comparison: an id, its label, and the value of the chosen figure for any period
 * (`null` where it has none). Both comparisons (members, branches) reduce to this, so the sort, the bar
 * scaling, the delta and the "Ohne Wert" fallback live in one place and cannot diverge.
 */
export interface ComparisonEntry {
  id: string;
  label: string;
  valueAt: (periodKey: string) => number | null;
  /** The cases behind the value; always `null` where they are the value itself (Projekte). */
  basisAt: (periodKey: string) => number | null;
}

/**
 * The shared core: a bar per entry that publishes the figure in the period, by value descending, the
 * rest named under "Ohne Wert", and an optional reference row. The two public builders differ only in how
 * they supply their entries and the reference.
 */
export function buildComparisonCore(
  metricId: string,
  entries: ComparisonEntry[],
  referenceValueAt: (periodKey: string) => number | null,
  referenceBasisAt: (periodKey: string) => number | null,
  referenceLabel: string,
  periodKey: string,
  comparisonPeriodKey: string | undefined,
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  spread: ReferenceSpread | null = null,
  comparisonScale = 1
): MiraComparisonView {
  const hasComparisonPeriod = comparisonPeriodKey !== undefined && comparisonPeriodKey !== "";

  const rows: MiraComparisonRow[] = [];
  const withoutValue: string[] = [];

  for (const entry of entries) {
    const value = entry.valueAt(periodKey);

    if (value === null) {
      withoutValue.push(entry.label);
      continue;
    }

    // A total against a longer baseline is scaled to the chosen period's length (2026-11-02).
    const baseline = comparisonPeriodKey ? entry.valueAt(comparisonPeriodKey) : null;
    const previous = baseline === null ? null : baseline * comparisonScale;
    const delta = comparisonDelta(value, previous, hasComparisonPeriod, formatValue, ctx);
    rows.push({
      id: entry.id,
      label: entry.label,
      value,
      valueText: formatValue(value),
      shareText: null,
      casesText: rowCasesText(metricId, entry.basisAt(periodKey), ctx),
      fraction: 0,
      deltaText: delta.text,
      deltaDirection: delta.direction,
      showDelta: delta.showDelta,
      reference: false,
    });
  }

  // The bars scale to the largest row value, so a row reads against its peers, not the reference total
  // (which for a sum is every row added up and would dwarf them all). With markers the scale also takes
  // the reference's P85, so no marker falls off the track.
  const largest = Math.max(0, ...rows.map((row) => Math.abs(row.value)), spread && rows.length ? spread.p85 : 0);
  rows.forEach((row) => {
    row.fraction = largest > 0 ? Math.abs(row.value) / largest : 0;
  });

  const direction = metricDirection(metricId);
  rows.sort((a, b) => compareBestFirst(direction, a.value, b.value) || a.label.localeCompare(b.label));
  withoutValue.sort((a, b) => a.localeCompare(b));

  const referenceValue = referenceValueAt(periodKey);
  // A total's rows are parts of the reference: each names its share of it, percentage first (X2).
  const total = isTotalMetric(metricId);
  if (total && referenceValue !== null && referenceValue > 0) {
    rows.forEach((row) => {
      row.shareText = formatPercent(row.value / referenceValue, ctx);
    });
  }
  const reference: MiraComparisonRow | null =
    referenceValue === null
      ? null
      : {
          id: MIRA_COMPARISON_REFERENCE_ID,
          label: referenceLabel,
          value: referenceValue,
          valueText: formatValue(referenceValue),
          shareText: null,
          casesText: rowCasesText(metricId, referenceBasisAt(periodKey), ctx),
          fraction: largest > 0 ? Math.min(1, Math.abs(referenceValue) / largest) : 0,
          // The reference is a context line, not a row, so it carries no change of its own.
          deltaText: null,
          deltaDirection: "flat",
          showDelta: hasComparisonPeriod,
          reference: true,
        };

  const markers: MiraComparisonMarkers | undefined =
    spread && rows.length && largest > 0
      ? {
          p15: spread.p15 / largest,
          median: spread.median / largest,
          p85: spread.p85 / largest,
          text: ctx.t("mira.comparison.markers", [formatValue(spread.p15), formatValue(spread.median), formatValue(spread.p85)]),
        }
      : undefined;

  return { reference, rows, withoutValue, ...(total ? { referenceWithoutBar: true } : {}), ...(markers ? { markers } : {}) };
}

/**
 * Build the comparison for one figure: the group total as the reference, one bar per member that
 * publishes the figure in the period, and the rest named under "Ohne Wert".
 *
 * @param metricId the figure selected on the screen.
 * @param ownDocument the signed-in client's document (the GROUP document on a master).
 * @param memberDocuments each member's own CLIENT document, keyed by id (`null` when it has none).
 * @param names each member's display name, keyed by id (the member roster to iterate).
 * @param periodKey the chosen period.
 * @param comparisonPeriodKey the period the chosen one compares to (`undefined` for none).
 * @param formatValue the screen's formatter for this figure, so every bar reads in the tile's unit.
 * @param ctx translation and number formatting.
 * @param referenceLabel the reference row's label ("Gruppe gesamt").
 * @param withSpread whether to mark the group's own P15 / median / P85 on the rows (`VIEW_MIRA_PERCENTILES`).
 * @param comparisonScale what the comparison period's value is multiplied by: a total against a longer baseline
 *   (`baselineScale`, 2026-11-02); 1 otherwise.
 */
export function buildComparison(
  metricId: string,
  ownDocument: MiraSectionDocument | null | undefined,
  memberDocuments: Record<string, MiraSectionDocument | null>,
  names: Record<string, string>,
  periodKey: string,
  comparisonPeriodKey: string | undefined,
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  referenceLabel: string,
  withSpread = false,
  comparisonScale = 1
): MiraComparisonView {
  // Every row names its cases in the figure's unit (S1); Projekte's cases are the value itself.
  const noCases = caseCountIsValue(metricId);
  const entries: ComparisonEntry[] = Object.keys(names).map((id) => ({
    id,
    label: names[id],
    valueAt: (key) => headlineValue(memberDocuments[id] ?? null, key, metricId),
    basisAt: (key) => (noCases ? null : headlineBasis(memberDocuments[id] ?? null, key, metricId)),
  }));
  return buildComparisonCore(
    metricId,
    entries,
    (key) => headlineValue(ownDocument, key, metricId),
    (key) => (noCases ? null : headlineBasis(ownDocument, key, metricId)),
    referenceLabel,
    periodKey,
    comparisonPeriodKey,
    formatValue,
    ctx,
    withSpread ? referenceSpread(ownDocument, periodKey, metricId) : null,
    comparisonScale
  );
}

/**
 * Build the branch comparison for one figure: the client's own total as the reference, one bar per branch
 * the document carries a value for in the period, and the rest named under "Ohne Wert".
 *
 * The rows are exactly the branches the **document** carries for the chosen period: a real branch id, the
 * {@link MIRA_BRANCH_NONE} bucket, or the {@link MIRA_BRANCH_OTHER} bucket. A branch present in the period
 * but not AVAILABLE for this figure lands under "Ohne Wert"; a branch the document does not carry is not
 * listed at all (it simply had nothing in the period, which is not "without value"). A figure the document
 * carries for no branch (DEV-006, PRJ-006/008, PHA-001/002) has no branch comparison: the view is flagged
 * {@link MiraComparisonView.noComparisonForMetric} so the card shows a reason line instead.
 *
 * @param metricId the figure selected on the screen.
 * @param branchDocument the client's `branches_{section}` document (`null` where it does not exist).
 * @param ownDocument the client's own `{section}` document, for the reference (total across all branches).
 * @param names each real branch's display name, keyed by branch id (`getBranchesOfClient`, deleted dropped).
 * @param periodKey the chosen period.
 * @param comparisonPeriodKey the period the chosen one compares to (`undefined` for none).
 * @param formatValue the screen's formatter for this figure.
 * @param ctx translation and number formatting.
 * @param referenceLabel the reference row's label (the client's own name, or "Gesamt").
 * @param withSpread whether to mark the company's own P15 / median / P85 on the rows (`VIEW_MIRA_PERCENTILES`).
 * @param comparisonScale what the comparison period's value is multiplied by: a total against a longer baseline
 *   (`baselineScale`, 2026-11-02); 1 otherwise.
 */
export function buildBranchComparison(
  metricId: string,
  branchDocument: MiraBranchDocument | null | undefined,
  ownDocument: MiraSectionDocument | null | undefined,
  names: Record<string, string>,
  periodKey: string,
  comparisonPeriodKey: string | undefined,
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  referenceLabel: string,
  withSpread = false,
  comparisonScale = 1
): MiraComparisonView {
  const branchIds = Object.keys(branchDocument?.periods[periodKey]?.branches ?? {});

  // A branch id the document carries but no name resolves for keeps its id as the key and reads as unknown;
  // the two reserved buckets read as their own labels. The key stays the id so rows are stable across names.
  const branchLabel = (id: string): string => {
    // A name given wins, so the per-insurer document's "Weitere Versicherer" is not called a branch (2026-11-03).
    if (names[id]) {
      return names[id];
    }
    if (id === MIRA_BRANCH_NONE) {
      return ctx.t("mira.comparison.branchNone");
    }
    if (id === MIRA_BRANCH_OTHER) {
      return ctx.t("mira.comparison.branchOther");
    }
    return names[id] ?? ctx.t("mira.comparison.branchUnknown");
  };

  // The figure is not carried for any branch (never written to the document): there is no branch comparison.
  // Listing every branch as "Ohne Wert" would be wrong, so flag it and let the card show a reason instead.
  const metricCarried = branchIds.some(
    (id) => branchDocument?.periods[periodKey]?.branches[id]?.[metricId] !== undefined
  );
  if (branchIds.length === 0 || !metricCarried) {
    return { reference: null, rows: [], withoutValue: [], noComparisonForMetric: true };
  }

  // As the member comparison: every row names its cases, except where they are the value.
  const noCases = caseCountIsValue(metricId);
  const entries: ComparisonEntry[] = branchIds.map((id) => ({
    id,
    label: branchLabel(id),
    valueAt: (key) => branchValue(branchDocument, key, id, metricId),
    basisAt: (key) => (noCases ? null : branchBasis(branchDocument, key, id, metricId)),
  }));
  return buildComparisonCore(
    metricId,
    entries,
    (key) => headlineValue(ownDocument, key, metricId),
    (key) => (noCases ? null : headlineBasis(ownDocument, key, metricId)),
    referenceLabel,
    periodKey,
    comparisonPeriodKey,
    formatValue,
    ctx,
    withSpread ? referenceSpread(ownDocument, periodKey, metricId) : null,
    comparisonScale
  );
}

/** The id of the Markt row, kept out of the client id space. */
export const MIRA_MARKET_PROJECTS_ID = "__market_projects__";
/** The company's own bar beside the Markt in the grouped bars. */
export const MIRA_MARKET_OWN_ID = "__market_own__";

/**
 * The Markt entities on every card, after the company's own: Markt je Projekt only. Markt je Mandant was
 * dropped (Hans, #2610112 X6a: "by project is clearer, more values available"); IRES Cloud still writes
 * `miraMarket/clients_*`, Office no longer reads it.
 */
export const MIRA_MARKET_ENTITY_ORDER: readonly string[] = [MIRA_MARKET_PROJECTS_ID];

/**
 * Build the Markt comparison for one figure (Hans, Track #2610072, decided by Taimoor on 2026-10-23): the
 * company's own value as the reference row and "Markt je Projekt" (every project of every published Mandant
 * once) as the row. "Markt je Mandant" is no longer shown (Hans, #2610112 X6a). Unlike the group
 * comparison, every row, the company's own included, carries its own P15 / median / P85 as markers on one
 * shared scale (`withSpread`, `VIEW_MIRA_PERCENTILES`). A Markt without the figure in the period is named
 * under "Ohne Wert".
 *
 * @param metricId the figure selected on the screen.
 * @param ownDocument the signed-in client's document (the GROUP document on a master).
 * @param projectsDocument `miraMarket/projects_{section}`, or `null`.
 * @param projectsLabel the Markt row's label.
 * @param periodKey the chosen period.
 * @param comparisonPeriodKey the period the chosen one compares to (`undefined` for none).
 * @param formatValue the screen's formatter for this figure.
 * @param ctx translation and number formatting.
 * @param ownLabel the reference row's label (the client's own name).
 * @param withSpread whether to draw every row's own P15 / median / P85.
 * @param comparisonScale what the comparison period's value is multiplied by: a total against a longer baseline
 *   (`baselineScale`, 2026-11-02); 1 otherwise.
 */
export function buildMarketComparison(
  metricId: string,
  ownDocument: MiraSectionDocument | null | undefined,
  projectsDocument: MiraSectionDocument | null | undefined,
  projectsLabel: string,
  periodKey: string,
  comparisonPeriodKey: string | undefined,
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  ownLabel: string,
  withSpread = false,
  comparisonScale = 1
): MiraComparisonView {
  const noCases = caseCountIsValue(metricId);
  const entries: ComparisonEntry[] = [
    {
      id: MIRA_MARKET_PROJECTS_ID,
      label: projectsLabel,
      valueAt: (key) => headlineValue(projectsDocument, key, metricId),
      basisAt: (key) => (noCases ? null : headlineBasis(projectsDocument, key, metricId)),
    },
  ];
  const view = buildComparisonCore(
    metricId,
    entries,
    (key) => headlineValue(ownDocument, key, metricId),
    (key) => (noCases ? null : headlineBasis(ownDocument, key, metricId)),
    ownLabel,
    periodKey,
    comparisonPeriodKey,
    formatValue,
    ctx,
    null,
    comparisonScale
  );

  // A Markt without the figure has it withheld for too few companies with enough cases.
  view.withoutValueKey = "mira.market.tooFewCompanies";
  // The broader scope first, the company's own last (#2610137), as in the bars.
  view.referenceLast = true;
  // The company and the Markt are two views of one figure, not parts of a total: the reference keeps its
  // bar and no row names a share.
  delete view.referenceWithoutBar;
  view.rows.forEach((row) => {
    row.shareText = null;
  });

  // One scale for the bars and every row's markers, so no marker falls off its track.
  const spreads: Record<string, ReferenceSpread | null> = {
    [MIRA_COMPARISON_REFERENCE_ID]: withSpread ? referenceSpread(ownDocument, periodKey, metricId) : null,
    [MIRA_MARKET_PROJECTS_ID]: withSpread ? referenceSpread(projectsDocument, periodKey, metricId) : null,
  };
  const shown = [...view.rows, ...(view.reference ? [view.reference] : [])];
  const largest = Math.max(
    0,
    ...shown.map((row) => Math.abs(row.value)),
    ...shown.map((row) => spreads[row.id]?.p85 ?? 0)
  );
  for (const row of shown) {
    row.fraction = largest > 0 ? Math.min(1, Math.abs(row.value) / largest) : 0;
    const spread = spreads[row.id];
    if (spread && largest > 0) {
      row.markers = {
        p15: spread.p15 / largest,
        median: spread.median / largest,
        p85: spread.p85 / largest,
        text: ctx.t("mira.comparison.markers", [formatValue(spread.p15), formatValue(spread.median), formatValue(spread.p85)]),
      };
    }
  }
  return view;
}

/**
 * The "über {contributing} von {total} Betrieben" caption suffix for a GROUP metric that carries its
 * member counts, or `null` where it does not (a CLIENT document, or an older GROUP document).
 */
export function membersSuffix(
  members: { total: number; contributing: number } | undefined,
  ctx: MiraViewContext
): string | null {
  if (!members) {
    return null;
  }
  return ctx.t("mira.comparison.membersSuffix", [ctx.formatNumber(members.contributing), ctx.formatNumber(members.total)]);
}
